export type KycRouteErrorKind = 'invalid-link' | 'temporary-unavailable';

type ErrorRecord = Record<string, unknown>;

const NETWORK_FAILURE_PATTERN = /failed to fetch|load failed|network\s*error|networkerror/i;

function asRecord(error: unknown): ErrorRecord | undefined {
  return typeof error === 'object' && error !== null ? (error as ErrorRecord) : undefined;
}

function getHttpStatus(error: unknown): number | undefined {
  const status = asRecord(error)?.httpStatus;
  return typeof status === 'number' && Number.isFinite(status) ? status : undefined;
}

function getResponseCorrelationId(error: unknown): string | undefined {
  const correlationId = asRecord(error)?.correlationId;
  return typeof correlationId === 'string' && correlationId ? correlationId : undefined;
}

function isNetworkFailure(error: unknown): boolean {
  const record = asRecord(error);
  const name = error instanceof Error ? error.name : record?.name;
  const message = error instanceof Error ? error.message : record?.message;
  return (
    name === 'NetworkError' || (error instanceof TypeError && typeof message === 'string' && NETWORK_FAILURE_PATTERN.test(message))
  );
}

export class KycRouteError extends Error {
  public readonly code: KycRouteErrorKind;
  public readonly httpStatus?: number;
  public readonly responseCorrelationId?: string;

  public constructor(code: KycRouteErrorKind, cause: unknown) {
    const httpStatus = getHttpStatus(cause);
    super(
      httpStatus === undefined
        ? code === 'invalid-link'
          ? 'Onboarding link is invalid or expired'
          : 'KYC status request failed because of a network error'
        : `KYC status request returned HTTP ${httpStatus}`
    );
    this.name = 'KycRouteError';
    this.code = code;
    this.httpStatus = httpStatus;
    this.responseCorrelationId = getResponseCorrelationId(cause);
  }
}

export function classifyKycStatusFailure(error: unknown): KycRouteError | undefined {
  const httpStatus = getHttpStatus(error);
  if (httpStatus === 401 || httpStatus === 403) return new KycRouteError('invalid-link', error);
  if ((httpStatus !== undefined && httpStatus >= 500 && httpStatus <= 599) || isNetworkFailure(error)) {
    return new KycRouteError('temporary-unavailable', error);
  }
  return undefined;
}

export function isKycRouteError(error: unknown): error is KycRouteError {
  if (error instanceof KycRouteError) return true;
  const record = asRecord(error);
  return record?.name === 'KycRouteError' && (record.code === 'invalid-link' || record.code === 'temporary-unavailable');
}
